/**
 * Environment gating utility (single source of truth for local / development / live).
 *
 * The deployed "development" site is a production build, so `process.env.NODE_ENV`
 * cannot tell it apart from live. Detection is therefore hostname-based, matching the
 * rest of the codebase (see `utils/subdomainDetection.ts`).
 *
 * Environments:
 *   - local:       hostname includes "localhost" or is "127.0.0.1"
 *   - development: not local AND hostname includes "development" (e.g. development.tweek.io)
 *   - live:        everything else
 *   - isNonProd:   local OR development (the common "hide on live" case)
 *
 * Usage examples:
 *
 * // 1. Booleans
 * // import { isNonProd, isLive } from "utils/environment";
 * // if (isNonProd()) { ...only runs on local + development... }
 *
 * // 2. Hide a nav item on live (conditional spread inside a nav array)
 * // ...(isNonProd()
 * //   ? [{ title: "Briefcase", link: "/mybriefcase", slug: "mybriefcase", activeIcon: "", inactiveIcon: "" }]
 * //   : []),
 *
 * // 3. Hide a button on live (show only on local + development)
 * //    a) Declarative wrapper:
 * //       import { EnvOnly } from "components/UI/EnvOnly/EnvOnly";
 * //       <EnvOnly envs={["local", "development"]}>
 * //         <button onClick={handleClick}>Experimental action</button>
 * //       </EnvOnly>
 * //    b) With the hook:
 * //       import { useEnv } from "hooks/useEnv";
 * //       const { isNonProd } = useEnv();
 * //       {isNonProd && <button onClick={handleClick}>Experimental action</button>}
 *
 * // 4. Route to a different page per environment
 * // import { pickByEnv } from "utils/environment";
 * // navigate(pickByEnv({ live: "/overview", development: "/overview-beta", local: "/overview-beta" }));
 *
 * // 5. Redirect away from a page entirely on live (e.g. lock it down)
 * // import { isLive } from "utils/environment";
 * // useEffect(() => {
 * //   if (isLive()) navigate("/overview", { replace: true });
 * // }, []);
 *
 * // 6. Preview another environment from localhost (dev aid) — see getEnvOverride:
 * //    visit  ...localhost:3000/?env=live  then reload to see the live experience.
 * //    reset with  ?env=local  or  clearEnvOverride().
 */

export type AppEnvironment = "local" | "development" | "live";

const ENV_OVERRIDE_KEY = "__appEnvOverride";

const isAppEnvironment = (value: unknown): value is AppEnvironment =>
  value === "local" || value === "development" || value === "live";

/** Environment derived purely from the hostname (no override applied). */
const getRealEnvironment = (): AppEnvironment => {
  const hostname = window.location.hostname;
  if (hostname.includes("localhost") || hostname === "127.0.0.1") return "local";
  if (hostname.includes("development")) return "development";
  return "live";
};

/**
 * Dev-only override so you can preview another environment's behavior WITHOUT
 * deploying (e.g. see the live experience straight from localhost).
 *
 * Set it by adding `?env=live` (or `?env=development` / `?env=local`) to the URL
 * once — it persists in localStorage — then reload. Because routes are built at
 * app startup, a full reload is required for route-level guards to pick it up.
 * Clear it with `?env=local`, clearEnvOverride(), or by clearing localStorage.
 *
 * SECURITY: the override is ignored when the real hostname is live, so hidden
 * features / route guards can never be unlocked on production.
 */
const getEnvOverride = (): AppEnvironment | null => {
  try {
    const fromQuery = new URLSearchParams(window.location.search).get("env");
    if (isAppEnvironment(fromQuery)) {
      window.localStorage.setItem(ENV_OVERRIDE_KEY, fromQuery);
      return fromQuery;
    }
    const stored = window.localStorage.getItem(ENV_OVERRIDE_KEY);
    if (isAppEnvironment(stored)) return stored;
  } catch {
    // localStorage can throw (private mode / SSR) — fall back to real env.
  }
  return null;
};

export const getAppEnvironment = (): AppEnvironment => {
  const real = getRealEnvironment();
  // Never honor an override on real live — hidden features must stay hidden.
  if (real === "live") return "live";
  return getEnvOverride() ?? real;
};

/** Programmatically set the dev-only environment override (reload to apply). */
export const setEnvOverride = (env: AppEnvironment) => {
  try {
    window.localStorage.setItem(ENV_OVERRIDE_KEY, env);
  } catch {
    /* no-op */
  }
};

/** Remove the dev-only environment override (reload to apply). */
export const clearEnvOverride = () => {
  try {
    window.localStorage.removeItem(ENV_OVERRIDE_KEY);
  } catch {
    /* no-op */
  }
};

export const isLocal = () => getAppEnvironment() === "local";
export const isDevelopment = () => getAppEnvironment() === "development";
export const isLive = () => getAppEnvironment() === "live";
export const isNonProd = () => !isLive();

/**
 * Select a value based on the current environment.
 *
 * `live` is required (the safe default). `local` and `development` are optional and
 * fall back: development -> local -> live when omitted. This lets callers express
 * "different on dev, same-as-live otherwise" concisely.
 *
 * // pickByEnv({ live: "/overview", development: "/overview-beta" })
 * //   -> "/overview-beta" on development and local, "/overview" on live
 */
export const pickByEnv = <T>(options: {
  local?: T;
  development?: T;
  live: T;
}): T => {
  const env = getAppEnvironment();
  if (env === "local") return options.local ?? options.live;
  if (env === "development")
    return options.development ?? options.local ?? options.live;
  return options.live;
};
